const DEBUG = typeof process !== 'undefined' && process.env?.REACT_APP_EXCEL_MERGE_DEBUG === 'true';

function colLetter(n) {
  let letter = '';
  let num = n;
  while (num > 0) {
    const mod = (num - 1) % 26;
    letter = String.fromCharCode(65 + mod) + letter;
    num = Math.floor((num - 1) / 26);
  }
  return letter;
}

export function normalizeRange(c1, r1, c2, r2) {
  return {
    c1: Math.min(c1, c2),
    r1: Math.min(r1, r2),
    c2: Math.max(c1, c2),
    r2: Math.max(r1, r2),
  };
}

export function colToNum(col) {
  let n = 0;
  const s = String(col || '').toUpperCase();
  for (let i = 0; i < s.length; i += 1) {
    n = n * 26 + (s.charCodeAt(i) - 64);
  }
  return n;
}

export function parseRangeAddress(address) {
  const raw = String(address || '').trim().toUpperCase();
  const parts = raw.split(':');
  const parseCell = (cell) => {
    const m = cell.match(/^([A-Z]+)(\d+)$/);
    if (!m) return null;
    return { col: colToNum(m[1]), row: Number(m[2]) };
  };
  const start = parseCell(parts[0]);
  if (!start) return null;
  const end = parts[1] ? parseCell(parts[1]) : { ...start };
  return normalizeRange(start.col, start.row, end.col, end.row);
}

export function rangeToAddress(range) {
  const n = normalizeRange(range.c1, range.r1, range.c2, range.r2);
  const start = `${colLetter(n.c1)}${n.r1}`;
  const end = `${colLetter(n.c2)}${n.r2}`;
  return start === end ? start : `${start}:${end}`;
}

export function rangesOverlap(a, b) {
  const x = normalizeRange(a.c1, a.r1, a.c2, a.r2);
  const y = normalizeRange(b.c1, b.r1, b.c2, b.r2);
  return !(x.c2 < y.c1 || y.c2 < x.c1 || x.r2 < y.r1 || y.r2 < x.r1);
}

export class MergeTracker {
  constructor(sheet, { debug = DEBUG, sheetName = '' } = {}) {
    this.sheet = sheet;
    this.debug = debug;
    this.sheetName = sheetName || sheet?.name || 'Sheet';
    /** @type {Map<string, { range: object, label: string }>} */
    this.merged = new Map();
    this.skipped = [];
    this._seedExisting();
  }

  _seedExisting() {
    const existing = this.sheet?.model?.merges || [];
    existing.forEach((addr) => {
      const parsed = parseRangeAddress(addr);
      if (parsed) {
        this.merged.set(rangeToAddress(parsed), { range: parsed, label: '(pre-existing)' });
      }
    });
  }

  _log(message, extra = {}) {
    if (!this.debug) return;
    // eslint-disable-next-line no-console
    console.log(`[ExcelMerge:${this.sheetName}] ${message}`, extra);
  }

  safeMerge(c1, r1, c2, r2, label = '') {
    const range = normalizeRange(c1, r1, c2, r2);
    if (range.c1 < 1 || range.r1 < 1 || range.c2 < range.c1 || range.r2 < range.r1) {
      this._log('skip invalid range', { label, range });
      return false;
    }

    const address = rangeToAddress(range);
    if (this.merged.has(address)) {
      this._log('skip duplicate', { label, address });
      return false;
    }

    for (const [existingAddr, meta] of this.merged.entries()) {
      if (rangesOverlap(range, meta.range)) {
        const conflict = { label, address, conflictsWith: existingAddr, conflictsLabel: meta.label };
        this.skipped.push(conflict);
        this._log('skip overlap', conflict);
        return false;
      }
    }

    try {
      this.sheet.mergeCells(address);
      this.merged.set(address, { range, label });
      this._log('merged', { label, address });
      return true;
    } catch (err) {
      this._log('merge failed', { label, address, error: err.message });
      this.skipped.push({ label, address, error: err.message });
      return false;
    }
  }

  getReport() {
    return {
      sheet: this.sheetName,
      merged: [...this.merged.keys()],
      skipped: this.skipped,
    };
  }
}

const TRACKER_KEY = '__deMergeTracker';

export function initMergeTracker(sheet, options = {}) {
  if (!sheet[TRACKER_KEY]) {
    sheet[TRACKER_KEY] = new MergeTracker(sheet, options);
  }
  return sheet[TRACKER_KEY];
}

export function getMergeTracker(sheet) {
  return sheet?.[TRACKER_KEY] || null;
}

export function safeMerge(sheet, c1, r1, c2, r2, label = '') {
  const tracker = sheet[TRACKER_KEY] || initMergeTracker(sheet);
  return tracker.safeMerge(c1, r1, c2, r2, label);
}

export function mergeRangeSafe(sheet, c1, r1, c2, r2, label = '') {
  return safeMerge(sheet, c1, r1, c2, r2, label);
}

export function flushMergeReports(workbook) {
  const reports = (workbook?.worksheets || []).map((ws) => getMergeTracker(ws)?.getReport()).filter(Boolean);
  if (DEBUG && reports.length) {
    // eslint-disable-next-line no-console
    console.table(reports.flatMap((r) => r.skipped.map((s) => ({ sheet: r.sheet, ...s }))));
  }
  return reports;
}
